import { Link } from 'react-router-dom';
import { useMyWork } from '../api/queries.ts';
import { Staleness } from '../components/Staleness.tsx';
import { LoadFailure, isSessionFailure } from '../components/LoadFailure.tsx';
import { formatDate } from '../dates.ts';
import type { WorkTask } from '../api/types.ts';

/** Worst first. Blocked last: it is waiting on something, not on the reader. */
const BUCKETS: WorkTask['bucket'][] = [
  'Overdue', 'This week', 'This month', 'Later', 'No date', 'Blocked',
];

/**
 * Tasks due — the caller's own work, linked from the client list.
 *
 * Built to be where case managers land, and not yet their landing page: see
 * the note on LANDING in App.tsx.
 *
 * The caller's open tasks, from `GET /me/work`, grouped by when they fall due.
 * **Tasks, never visit priority.** A task is a piece of work someone holds:
 * a filing from a deadline, or a welfare visit a task was raised for
 * (`refresh_visit_tasks()`). How far behind a client is against the year's
 * visit quota is a different measure — `v_visit_arrears` — and nothing here
 * should read as if it were that, even when most of the rows are visits.
 *
 * A failure is never shown as an empty list. "No open tasks" appears only when
 * the server answered and there were none.
 */
export function Today() {
  const work = useMyWork();

  const failure = work.error;
  const takeover = failure !== null && (isSessionFailure(failure) || !work.data);
  const tasks = work.data?.tasks ?? [];
  const total = work.data?.total ?? 0;

  return (
    <div className="min-h-dvh bg-neutral-50">
      <div className="mx-auto max-w-[96rem] lg:px-6 lg:py-6">
        <header className="border-b border-neutral-200 bg-white px-4 pt-3 pb-2 lg:rounded-t-lg
                           lg:border-b-0 lg:px-5 lg:pt-4 lg:shadow">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-bold text-neutral-900">Tasks due</h1>
            <p className="text-sm text-neutral-500 tabular-nums">
              {takeover ? 'Not loaded' : work.isPending ? 'Loading…' : `${total} open`}
            </p>
            <Link
              to="/clients"
              className="ml-auto inline-flex min-h-[44px] items-center rounded-lg px-3
                         text-[0.9375rem] font-semibold text-neutral-600 hover:text-neutral-900"
            >
              Clients
            </Link>
          </div>
          <p className="mt-0.5 text-sm text-neutral-500">
            Open tasks assigned to you, soonest due first.
          </p>
        </header>

        <div className="bg-white lg:rounded-b-lg lg:shadow">
          {!takeover && (
            <Staleness
              updatedAt={work.dataUpdatedAt}
              isFetching={work.isFetching}
              error={work.error}
              onRefresh={() => void work.refetch()}
            />
          )}

          {failure && takeover ? (
            <LoadFailure
              what="Your tasks"
              error={failure}
              onRetry={() => void work.refetch()}
              retrying={work.isFetching}
            />
          ) : work.isSuccess && tasks.length === 0 ? (
            <p className="px-4 py-10 text-center text-neutral-500">
              No open tasks are assigned to you.
            </p>
          ) : (
            <>
              {BUCKETS.map((bucket) => {
                const inBucket = tasks.filter((t) => t.bucket === bucket);
                if (inBucket.length === 0) return null;
                return (
                  <section key={bucket}>
                    <h2 className={`border-t border-neutral-200 px-4 pt-3 pb-1 text-xs font-bold
                                    tracking-[0.14em] uppercase lg:px-5 ${
                                      bucket === 'Overdue' ? 'text-critical-edge' : 'text-neutral-500'}`}>
                      {bucket} · {inBucket.length}
                    </h2>
                    <ol className="divide-y divide-neutral-200">
                      {inBucket.map((t) => <TaskRow key={t.task_id} task={t} />)}
                    </ol>
                  </section>
                );
              })}
              {total > tasks.length && (
                <p className="border-t border-neutral-200 px-4 py-3 text-sm text-neutral-500">
                  Showing the {tasks.length} soonest due of {total}.
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function TaskRow({ task: t }: { task: WorkTask }) {
  const when = t.due_date === null
    ? 'No due date'
    : `${t.due_date_provisional ? 'about ' : ''}${formatDate(t.due_date)}`;

  const body = (
    <div className="flex gap-3 lg:items-center">
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-neutral-900">{t.title}</p>
        {t.client_name && (
          <p className="mt-0.5 truncate text-[0.9375rem] text-neutral-600">
            {t.client_name}
            {t.client_ref && <span className="font-mono text-sm text-neutral-500"> · {t.client_ref}</span>}
          </p>
        )}
        {t.status === 'Blocked' && t.blocked_reason && (
          <p className="mt-0.5 text-sm text-neutral-600">Blocked: {t.blocked_reason}</p>
        )}
      </div>
      <div className="shrink-0 text-right">
        <p className="text-sm text-neutral-700 tabular-nums">{when}</p>
        {t.bucket === 'Overdue' && t.days_overdue !== null && t.days_overdue > 0 && (
          <p className="text-sm font-semibold text-critical-edge tabular-nums">
            {t.days_overdue} day{t.days_overdue === 1 ? '' : 's'} overdue
          </p>
        )}
        {t.is_statutory && (
          <p className="text-xs font-bold tracking-wide text-neutral-500 uppercase">Statutory</p>
        )}
        {t.due_date_provisional && (
          <p className="text-xs text-neutral-500">estimate</p>
        )}
      </div>
    </div>
  );

  return (
    <li data-quick-client={t.client_id ?? undefined}>
      {t.client_id ? (
        <Link
          to={`/clients/${t.client_id}`}
          state={{ from: 'Tasks due' }}
          className="block min-h-[64px] px-4 py-3 hover:bg-neutral-50 active:bg-neutral-100 lg:px-5"
        >
          {body}
        </Link>
      ) : (
        <div className="min-h-[64px] px-4 py-3 lg:px-5">{body}</div>
      )}
    </li>
  );
}
